import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Form, Button, Radio, Card, Space, Typography, Divider, notification, Modal } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OrderService from "services/order";
import thirdPartyIntegrationService from "services/thirdPartyIntegrationService";
import { THIRDPARTY_CODE_IMAGE } from "constants/app";
import { PAYMENT_METHODS } from "constants/order";
import { PaymentMethodsColumn, PaymentInfoColumn, PaymentSummaryColumn, ConfirmCashModal, ConfirmQRSuccessModal } from "./_components/PaymentComponents";
import Service from "services/request";

const { Text } = Typography;
// Không cần BAOKIM_PAYMENT_METHOD_TYPE, dùng trực tiếp số 11
const PAYMENT_FLOW_STATUS = {
  IDLE: 'idle',
  LOADING_QR: 'loading_qr',
  PROCESSING_LINK: 'processing_link',
};

const OrderPaymentPage = () => {
  const { t: translation } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState(null);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [selectedPaymentMethodObj, setSelectedPaymentMethodObj] = useState(null);
  const [qrResult, setQrResult] = useState(null);
  const [paymentFlow, setPaymentFlow] = useState(PAYMENT_FLOW_STATUS.IDLE);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [isCashModalVisible, setIsCashModalVisible] = useState(false);
  const [isQRSuccessModalVisible, setIsQRSuccessModalVisible] = useState(false);
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const hasAutoSelected = useRef(false);
  const hasLoadedPaymentMethods = useRef(false);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    const pendingOrderId = localStorage.getItem('pendingOrderId');
    if (pendingOrderId) {
      setOrderId(pendingOrderId);
      localStorage.removeItem('pendingOrderId');
    } else {
      history.push('/orders?tab=1');
    }
  }, [history]);

  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const getPaymentMethodKeyFromPartyCode = (partyCode) => {
    const partyCodeMapping = {
      'BANKING_VIETQR': '1',
      'GTELPAY_GATEWAY': '3',
      'VNPAY_BUSINESS': '4',
      'VNPAYAPP_INAPP': '13',
      'ZALOPAY_BUSINESS': '5',
      'SHOPEEPAY': '10',
      'BAOKIM_BUSINESS': '11',
      'KIMNGAN_BUSINESS': '12',
    };
    return partyCodeMapping[partyCode];
  };

  // Các cổng được phép hiển thị trên trang thanh toán web Station
  const WEB_SUPPORTED_PARTY_CODES = new Set([
    'BANKING_VIETQR',
    'GTELPAY_GATEWAY',
    'MOMO_BUSINESS',
    'VNPAY_BUSINESS',
    'ZALOPAY_BUSINESS',
    'SHOPEEPAY',
    'BAOKIM_BUSINESS',
    'KIMNGAN_BUSINESS',
  ]);

  // Lấy các phương thức thanh toán khả dụng cho trạm này
  useEffect(() => {
    if (hasLoadedPaymentMethods.current) return;
    const cashItem = {
      id: 'cash',
      integrationId: null,
      paymentMethodType: 0,
      name: translation("order.cash"),
      logoUrl: null,
      description: '',
      raw: null,
      isConnected: true,
    };

    const currentStationId = orderData?.stationsId;
    if (!currentStationId) return;
    hasLoadedPaymentMethods.current = true;

    thirdPartyIntegrationService.getConfigsTelegram({ filter: {} })
      .then((data) => {
        if (data?.data) {
          const integrations = data.data.filter((integration) =>
            Number(integration?.stationsId) === Number(currentStationId)
          );

          // Lọc dữ liệu để lấy phương thức thanh toán hợp lệ
          const paymentMethodsFromAPI = [];
          integrations.forEach(integration => {
            if (!WEB_SUPPORTED_PARTY_CODES.has(integration?.partyCode)) {
              return;
            }
            if (
              process.env.REACT_APP_THEME_NAME !== 'TAMOVE' &&
              ['BAOKIM_BUSINESS', 'KIMNGAN_BUSINESS'].includes(integration?.partyCode)
            ) {
              return;
            }
            // Parse integrationMetadata nếu là chuỗi
            let meta = integration.integrationMetadata;
            if (typeof meta === 'string') {
              try {
                meta = JSON.parse(meta);
              } catch (e) {
                meta = {};
              }
            }
            if (!meta || typeof meta !== 'object' || Array.isArray(meta)) {
              meta = {};
            }
            integration.integrationMetadata = meta;

            let requiredData = {};
            if (integration.partyRequiredData) {
              try {
                requiredData = JSON.parse(integration.partyRequiredData);
              } catch (e) {
                requiredData = {};
              }
            }
            integration.partyRequiredData = requiredData;
            if (
              !requiredData ||
              typeof requiredData !== 'object' ||
              Array.isArray(requiredData) ||
              !Object.values(requiredData).some((v) => v !== undefined && v !== null && `${v}`.trim() !== '')
            ) {
              return;
            }

            const paymentMethodKey = getPaymentMethodKeyFromPartyCode(integration.partyCode);
            if (!paymentMethodKey) {
              return;
            }
            const methodMapping = PAYMENT_METHODS[paymentMethodKey];
            if (!methodMapping) {
              return;
            }

            if (integration.partyCode === 'ZALOPAY_BUSINESS' && Number(meta?.enableQrDisplay) !== 1) {
              return;
            }

            const rawData = {};

            // Thêm thông tin ngân hàng chỉ cho VietQR
            if (integration.partyCode === 'BANKING_VIETQR') {
              rawData.paymentMethodReceiverName = meta.userBankName || '';
              rawData.paymentMethodIdentityNumber = meta.bankAccount || '';
              rawData.paymentMethodBankCode = meta.bankCode || '';
              rawData.paymentMethodReferName = methodMapping.label; // Sử dụng label từ PAYMENT_METHODS
            }

            paymentMethodsFromAPI.push({
              id: methodMapping.paymentMethodId,
              integrationId: integration.thirdPartyId,
              paymentMethodType: methodMapping.paymentMethodType,
              name: methodMapping.label,
              logoUrl: THIRDPARTY_CODE_IMAGE[integration.partyCode] || meta.logoUrl || integration.logoUrl,
              description: meta.description || `Thanh toán qua ${methodMapping.label}`,
              raw: rawData,
              isConnected: integration.partyActiveStatus === 1 && integration.partyEnableStatus === 1,
              integration: integration,
            });
          });

          // Lọc theo loại được hỗ trợ và trạng thái kết nối
          const filteredMethods = paymentMethodsFromAPI.filter(method => 
            method.isConnected === true
          );

          const finalList = [...filteredMethods, cashItem];
          setPaymentMethods(finalList);
        } else {
          setPaymentMethods([cashItem]);
        }
      })
      .catch(() => {
        setPaymentMethods([cashItem]);
      });
  }, [translation, orderData]);

  // Lấy dữ liệu đơn hàng
  useEffect(() => {
    if (!orderId) {
      setFetchLoading(false);
      return;
    }
    setFetchLoading(true);
    OrderService.getListById({ id: orderId })
      .then((res) => {
        if (res) {
          setOrderData(res);
          // Kiểm tra trạng thái ngay sau khi lấy
          if (res.orderStatus === 30 || res.orderStatus === 20) {
            notification['warning']({
              message: "",
              description: translation("order.orderCompletedOrCancelled")
            });
            history.push('/orders?tab=1');
            return;
          }
        } else {
          setOrderData(null);
        }
      })
      .catch(() => {
        setOrderData(null);
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, [orderId, history]);

  const handleSelectPaymentMethod = async (pm) => {
    if (paymentFlow !== PAYMENT_FLOW_STATUS.IDLE) return;
    setPaymentFlow(PAYMENT_FLOW_STATUS.IDLE);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    setSelectedPaymentMethodId(pm.id);
    setSelectedPaymentMethodObj(pm);
    setSelectedPaymentMethodType(pm.paymentMethodType);
    setPaymentMethod(pm.name || "");

    if (pm.id !== 'cash' && orderId) {
      setPaymentFlow(PAYMENT_FLOW_STATUS.LOADING_QR);
      let isRedirectToPaymentLink = false;
      try {
        // Dùng paymentMethodId trực tiếp từ phương thức được chọn
        const paymentMethodId = pm.id;
        const isRedirectPayment = [3, 11, 12, 13].includes(Number(pm?.paymentMethodType));

        if (isRedirectPayment) {
          setPaymentFlow(PAYMENT_FLOW_STATUS.PROCESSING_LINK);
        }

        await OrderService.updateOrder({
          id: orderId,
          data: {
            paymentMethodId: paymentMethodId,
            paymentMethodType: pm.paymentMethodType,
          },
        });

        setOrderData((prev) => ({
          ...(prev || {}),
          paymentMethodId,
          paymentMethodType: pm.paymentMethodType,
        }));

        // Sau đó yêu cầu QR cho đơn hàng đã cập nhật
        const resp = await OrderService.getPaymentInfo({
          id: orderId,
          paymentMethodId: paymentMethodId,
          paymentMethodType: pm.paymentMethodType,
        });
        if (resp) {
          const paymentLinkUrl = resp?.paymentData?.paymentLinkUrl;

          if (isRedirectPayment) {
            setQrResult(null);
            if (paymentLinkUrl) {
              isRedirectToPaymentLink = true;
              window.location.href = paymentLinkUrl;
            } else {
              notification['warning']({
                message: "",
                description: "Không lấy được link thanh toán",
              });
            }
            return;
          }

          setQrResult(resp);
          // Bắt đầu polling để kiểm tra trạng thái thanh toán
          if (pollingInterval) clearInterval(pollingInterval);
          const interval = setInterval(async () => {
            try {
              const orderResp = await OrderService.getListById({ id: orderId });
              if (orderResp && orderResp.paymentStatus === 'Success') {
                // Cập nhật trạng thái đơn thành hoàn thành khi thanh toán thành công
                await updateOrderStatusOnPaymentSuccess(orderId, pm.name, translation("order.qrPaymentSuccess"));
                clearInterval(interval);
                setPollingInterval(null);
                setIsQRSuccessModalVisible(true);
                setQrResult(null);
              }
            } catch (error) {
              console.error('Polling error:', error);
            }
          }, 5000);
          setPollingInterval(interval);
        } else {
          setQrResult(null);
        }
      } catch (error) {
        setQrResult(null);
        notification['error']({
          message: "",
          description: translation("order.updatePaymentMethodError")
        });
      } finally {
        if (!isRedirectToPaymentLink) {
          setPaymentFlow(PAYMENT_FLOW_STATUS.IDLE);
        }
      }
    } else if (pm.id === 'cash' && orderId) {
      // người dùng chọn tiền mặt -> lưu paymentMethodId = 0 trên server
      setPaymentFlow(PAYMENT_FLOW_STATUS.LOADING_QR);
      try {
        const paymentMethodId = 0;
        const paymentMethodType = 0;
        await OrderService.updateOrder({ 
          id: orderId, 
          data: { 
            paymentMethodId,
            paymentMethodType,
          } 
        });
        setOrderData((prev) => ({ ...(prev || {}), paymentMethodId, paymentMethodType }));
      } catch (error) {
        notification['error']({
          message: "",
          description: translation("order.updatePaymentMethodError")
        });
      } finally {
        setPaymentFlow(PAYMENT_FLOW_STATUS.IDLE);
        setQrResult(null);
      }
    } else {
      setPaymentFlow(PAYMENT_FLOW_STATUS.IDLE);
      setQrResult(null);
    }
  };

  useEffect(() => {
    if (!orderData || !paymentMethods || paymentMethods.length === 0 || hasAutoSelected.current) return;

    const pid = orderData.paymentMethodId;
    if (pid !== undefined && pid !== null) {
      if (Number(pid) === 0) {
        setSelectedPaymentMethodId('cash');
        setSelectedPaymentMethodType(0);
        setPaymentMethod(translation("order.cash"));
        hasAutoSelected.current = true;
        return;
      }

      const found = paymentMethods.find(pm => pm.id === pid || Number(pm.id) === Number(pid));
      if (found) {
        setSelectedPaymentMethodId(found.id);
        setSelectedPaymentMethodType(found.paymentMethodType);
        setSelectedPaymentMethodObj(found);
        setPaymentMethod(found.name || '');
        handleSelectPaymentMethod(found);
        hasAutoSelected.current = true;
      }
    }
  }, [orderData, paymentMethods]);

  if (fetchLoading) {
    return (
      <div className="op-loading-container">
        <Text>{translation("order.loadingOrderData")}</Text>
      </div>
    );
  }

  if (!orderData) {
    return (
      <div className="op-loading-container">
        <Text type="danger">{translation("order.noOrderData")}</Text>
      </div>
    );
  }

  const selectedProducts = orderData.orderItems?.map((item) => ({
    productId: item.productId,
    orderQuantity: item.quantity,
    product: {
      productId: item.productId,
      productName: item.orderItemName,
      productPrice: item.productPrice,
    },
  })) || [];

  const subtotal = orderData.subTotalAmount || 0;
  const tax = orderData.taxAmount || 0;
  const fee = orderData.feeAmount || 0;
  const total = orderData.totalAmount || 0;

  const userData = {
    firstName: orderData.firstName || "",
    phoneNumber: orderData.phoneNumber || "",
    userHomeAddress: orderData.userHomeAddress || "",
    appUserIdentity: orderData.appUserIdentity || "",
  };

  const updateOrderStatusOnPaymentSuccess = async (orderId, paymentMethod, note = "") => {
    try {
      await OrderService.updateOrder({
        id: orderId,
        data: {
          orderStatus: 30, // Completed
          note: note || `Thanh toán ${paymentMethod} thành công`,
        },
      });
    } catch (error) {
      notification['error']({
        message: "",
        description: translation("order.updatePaymentStatusError")
      });
    }
  };

  const handleSubmitOrder = async () => {
    if (selectedPaymentMethodType == null) {
      notification['error']({
        message: "",
        description: translation("order.choosePaymentMethod")
      });
      return;
    }

    // Mở modal xác nhận cho thanh toán tiền mặt
    if (selectedPaymentMethodType === 0) {
      setIsCashModalVisible(true);
      return;
    }

    // Cho các phương thức QR, kiểm tra trạng thái thanh toán trước
    setLoading(true);
    try {
      const orderData = await OrderService.getListById({ id: orderId });
      if (orderData?.paymentStatus === "Success") {
        // Cập nhật trạng thái đơn thành hoàn thành
        await updateOrderStatusOnPaymentSuccess(orderId, paymentMethod, translation("order.qrPaymentSuccess"));
        setIsQRSuccessModalVisible(true);
        localStorage.removeItem('pendingOrderId');
      } else {
        notification['warning']({
          message: "",
          description: translation("order.paymentNotCompleted")
        });
      }
    } catch (error) {
      notification['error']({
        message: "",
        description: translation("order.checkPaymentStatusError")
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = () => {
    history.goBack();
  };

  const handleCashModalSuccess = async () => {
    setIsCashModalVisible(false);
    setLoading(true);
    try {
      // Update payment status to Success
      await OrderService.updateOrder({
        id: orderId,
        data: {
          paymentStatus: "Success",
          note: "Thanh toán tiền mặt thành công",
        },
      });
      // Cập nhật trạng thái đơn thành hoàn thành
      await updateOrderStatusOnPaymentSuccess(orderId, "tiền mặt", translation("order.cashPaymentSuccess"));
      setIsQRSuccessModalVisible(true);
      localStorage.removeItem('pendingOrderId');
    } catch (error) {
      notification['error']({
        message: "",
        description: "Có lỗi xảy ra khi cập nhật trạng thái thanh toán"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCashModalCancel = () => {
    setIsCashModalVisible(false);
  };

  const handleCancelOrder = () => {
    setIsCancelModalVisible(true);
  };

  const confirmCancelOrder = async () => {
    setIsCancelModalVisible(false);
    setLoading(true);
    try {
      await OrderService.updateOrder({
        id: orderId,
        data: {
          orderStatus: 20, // CANCELED
          note: "Đơn hàng đã bị hủy bởi người dùng",
        },
      });
      notification['success']({
        message: "",
        description: translation("order.orderCancelled")
      });
      localStorage.removeItem('pendingOrderId');
      history.push("/orders");
    } catch (error) {
      notification['error']({
        message: "",
        description: translation("order.cancelOrderError")
      });
    } finally {
      setLoading(false);
    }
  };

  if (!orderId) {
    return <div>{translation("order.invalidOrderId")}</div>;
  }

  if (fetchLoading) return <div>{translation("order.loadingOrderData")}</div>;

  return (
    <div className="op-main-container">
      <Form layout="vertical">
        <div className="op-content">
          <Row gutter={24} align="stretch">
              <Col xs={24} md={8} className="op-col-left">
                <PaymentMethodsColumn
                  paymentMethods={paymentMethods}
                  selectedPaymentMethodId={selectedPaymentMethodId}
                  onSelect={handleSelectPaymentMethod}
                  goToSettings={() => history.push('/setting?permission=camera&tab=SETTING_EXPAND')}
                  isLoading={paymentFlow !== PAYMENT_FLOW_STATUS.IDLE}
                />
              </Col>

            <Col xs={24} md={10} className="op-col-center">
              <PaymentInfoColumn
                userData={userData}
                paymentMethod={paymentMethod}
                subtotal={subtotal}
                tax={tax}
                fee={fee}
                total={total}
                qrResult={qrResult}
                paymentMethodObj={selectedPaymentMethodObj}
                qrLoading={paymentFlow !== PAYMENT_FLOW_STATUS.IDLE}
                loadingText={paymentFlow === PAYMENT_FLOW_STATUS.PROCESSING_LINK ? 'Đang thực hiện thanh toán' : null}
                selectedPaymentMethodId={selectedPaymentMethodId}
                orderId={orderId}
                paymentMethodType={selectedPaymentMethodType}
                enableQrDisplay={selectedPaymentMethodObj?.integration?.integrationMetadata?.enableQrDisplay || 0}
              />
            </Col>

            {/* Cột 3: Thông tin đơn hàng + tóm tắt */}
            <Col xs={24} md={6} className="op-col-right">
              <PaymentSummaryColumn
                selectedProducts={selectedProducts}
                userData={userData}
                subtotal={subtotal}
                tax={tax}
                fee={fee}
                total={total}
                orderCode={orderData.orderCode || orderData.orderId || orderId}
                createdAt={orderData.createdAt ? new Date(orderData.createdAt).toLocaleString('vi-VN') : (orderData.createdAtDisplay || '')}
              />
            </Col>
          </Row>
        </div>

        {/* 3 nút hành động trên */}
        <Row gutter={12} className="op-action-row">
          <Col xs={24} sm={5} style={{ marginBottom: '10px' }}>
            <Button 
              block 
              size="default" 
              onClick={handleUpdateOrder}
            >
              {translation("back")}
            </Button>
          </Col>
          <Col xs={24} sm={5} style={{ marginBottom: '10px' }}>
            <Button 
              block 
              size="default" 
              onClick={() => {
                const basePath = location.pathname.split('?')[0];
                history.replace(`${basePath}?tab=2`);
              }}
            >
              {translation("order.saveHistory")}
            </Button>
          </Col>
          <Col xs={24} sm={5}>
            <Button
              block
              size="default"
              type={selectedPaymentMethodType != null ? "primary" : "default"}
              loading={loading}
              disabled={selectedPaymentMethodType == null}
              className={selectedPaymentMethodType == null ? "co-button-disabled" : ""}
              onClick={handleSubmitOrder}
              style={{ minWidth: '130px' }}
            >
              {translation("order.paid")}
            </Button>
          </Col>
        </Row>

        {/* Nút hủy đơn hàng ở dưới, căn giữa */}
        <Row className="op-cancel-row">
          <Col xs={24} sm={5}>
            <Button 
              block 
              size="default" 
              danger 
              onClick={handleCancelOrder}
              style={{ minWidth: '130px' }}
            >
              {translation("order.cancelOrder")}
            </Button>
          </Col>
        </Row>
      </Form>

      <ConfirmCashModal
        isModalVisible={isCashModalVisible}
        onCancel={handleCashModalCancel}
        onSuccess={handleCashModalSuccess}
        orderId={orderId}
        orderCode={orderData?.orderCode || orderData?.orderId || orderId}
        selectedPaymentMethodId={selectedPaymentMethodId}
      />

      <ConfirmQRSuccessModal
        isModalVisible={isQRSuccessModalVisible}
        paymentMethod={paymentMethod}
        paymentMethodId={orderData?.paymentMethodId ?? (selectedPaymentMethodId === 'cash' ? 0 : selectedPaymentMethodId)}
        total={total}
        loading={loading}
        history={history}
        customerData={{
          firstName: orderData?.firstName || "",
          phoneNumber: orderData?.phoneNumber || "",
          userHomeAddress: orderData?.userHomeAddress || "",
          appUserIdentity: orderData?.appUserIdentity || "",
        }}
        onClose={() => setIsQRSuccessModalVisible(false)}
      />

      <Modal
        title={
            <div className="ps-modal-title">{translation("order.cancelOrderConfirmTitle")}</div>
        }
        visible={isCancelModalVisible}
        onCancel={() => setIsCancelModalVisible(false)}
        footer={null}
        centered
      >
        <div className="ps-modal-body-text">
          <Text className="ps-body-text">
            {translation("order.confirmCancelOrder")}
          </Text>
        </div>
        <div className="ps-modal-actions">
          <Button 
            onClick={() => setIsCancelModalVisible(false)} 
            className="ps-modal-cancel-btn"
            disabled={loading}
          >
            {translation("product.cancel")}
          </Button>
          <Button 
            type="primary"
            onClick={confirmCancelOrder} 
            loading={loading}
            className="ps-modal-delete-btn"
          >
            {translation("product.confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default OrderPaymentPage;
